import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MessageCircle, CreditCard, FileCheck, LogIn, LogOut, Sparkles, Wrench,
  StickyNote, ShieldCheck, Plus, Ban, Clock,
} from "lucide-react";

const FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "communication", label: "Comunicazioni" },
  { key: "payment", label: "Pagamenti" },
  { key: "document", label: "Documenti" },
  { key: "checkinout", label: "Check-in / Check-out" },
  { key: "cleaning", label: "Pulizie" },
  { key: "maintenance", label: "Manutenzioni" },
  { key: "note", label: "Note operative" },
  { key: "audit", label: "Audit" },
];

const CAT_STYLE = {
  communication: { icon: MessageCircle, dot: "bg-emerald-500", ring: "ring-emerald-100", text: "text-emerald-600" },
  payment: { icon: CreditCard, dot: "bg-indigo-500", ring: "ring-indigo-100", text: "text-indigo-600" },
  document: { icon: FileCheck, dot: "bg-sky-500", ring: "ring-sky-100", text: "text-sky-600" },
  checkin: { icon: LogIn, dot: "bg-teal-500", ring: "ring-teal-100", text: "text-teal-600" },
  checkout: { icon: LogOut, dot: "bg-orange-500", ring: "ring-orange-100", text: "text-orange-600" },
  cleaning: { icon: Sparkles, dot: "bg-cyan-500", ring: "ring-cyan-100", text: "text-cyan-600" },
  maintenance: { icon: Wrench, dot: "bg-rose-500", ring: "ring-rose-100", text: "text-rose-600" },
  note: { icon: StickyNote, dot: "bg-amber-500", ring: "ring-amber-100", text: "text-amber-600" },
  audit: { icon: ShieldCheck, dot: "bg-slate-400", ring: "ring-slate-100", text: "text-slate-500" },
};

const PRIORITY_STYLE = {
  urgente: "bg-rose-100 text-rose-700",
  alta: "bg-orange-100 text-orange-700",
  media: "bg-amber-100 text-amber-800",
  bassa: "bg-slate-100 text-slate-600",
};

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
};
const fmtAmount = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

export function Timeline({
  events = [],
  loading = false,
  noteCategories = [],
  notePriorities = [],
  canAddNote = false,
  canVoid = false,
  onAddNote,
  onVoidNote,
}) {
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteCat, setNoteCat] = useState(noteCategories[0] || "operativa");
  const [notePrio, setNotePrio] = useState("");
  const [saving, setSaving] = useState(false);

  const counts = useMemo(() => {
    const c = { all: events.length };
    events.forEach((e) => {
      const k = e.cat === "checkin" || e.cat === "checkout" ? "checkinout" : e.cat;
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [events]);

  const visible = useMemo(() => {
    if (filter === "all") return events;
    if (filter === "checkinout") return events.filter((e) => e.cat === "checkin" || e.cat === "checkout");
    return events.filter((e) => e.cat === filter);
  }, [events, filter]);

  const submitNote = async () => {
    if (!noteText.trim() || !onAddNote) return;
    setSaving(true);
    try {
      await onAddNote({ text: noteText.trim(), note_category: noteCat, priority: notePrio });
      setNoteText(""); setNotePrio(""); setAdding(false);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="timeline">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase text-slate-400">
          <Clock size={14} /> Cronologia eventi ({events.length})
        </h4>
        {canAddNote && (
          <Button size="sm" variant="outline" data-testid="timeline-add-note-toggle"
            className="h-8 gap-1.5 text-xs" onClick={() => setAdding((v) => !v)}>
            <Plus size={14} /> Nota operativa
          </Button>
        )}
      </div>

      {/* Filtri categoria */}
      <div className="mb-4 flex flex-wrap gap-1.5" data-testid="timeline-filters">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          const n = counts[f.key] || 0;
          return (
            <button key={f.key} data-testid={`timeline-filter-${f.key}`} onClick={() => setFilter(f.key)}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                active ? "border-primary bg-primary text-white" : "border-slate-200 bg-white text-slate-500 hover:border-primary hover:text-primary"
              }`}>
              {f.label}{f.key !== "all" && n > 0 ? ` · ${n}` : ""}
            </button>
          );
        })}
      </div>

      {/* Form nuova nota (append-only) */}
      {adding && canAddNote && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="timeline-note-form">
          <Textarea data-testid="timeline-note-text" value={noteText} onChange={(e) => setNoteText(e.target.value)}
            placeholder="Scrivi una nota operativa (non modificabile una volta salvata)…" className="mb-2 bg-white text-sm" rows={2} />
          <div className="flex flex-wrap items-center gap-2">
            <Select value={noteCat} onValueChange={setNoteCat}>
              <SelectTrigger className="h-8 w-40 bg-white text-xs" data-testid="timeline-note-category">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                {(noteCategories.length ? noteCategories : ["operativa"]).map((c) => (
                  <SelectItem key={c} value={c} className="text-xs capitalize">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={notePrio || "none"} onValueChange={(v) => setNotePrio(v === "none" ? "" : v)}>
              <SelectTrigger className="h-8 w-40 bg-white text-xs" data-testid="timeline-note-priority">
                <SelectValue placeholder="Priorità" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-xs">Nessuna priorità</SelectItem>
                {notePriorities.filter(Boolean).map((p) => (
                  <SelectItem key={p} value={p} className="text-xs capitalize">{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" data-testid="timeline-note-save" disabled={!noteText.trim() || saving}
              onClick={submitNote} className="h-8 gap-1.5 text-xs">
              {saving ? "Salvataggio…" : "Aggiungi nota"}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="py-8 text-center text-sm text-slate-400">Caricamento cronologia…</p>
      ) : visible.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-400" data-testid="timeline-empty">Nessun evento in questa categoria</p>
      ) : (
        <div className="relative max-h-96 space-y-0 overflow-y-auto pl-1" data-testid="timeline-list">
          {visible.map((e, i) => {
            const st = CAT_STYLE[e.cat] || CAT_STYLE.audit;
            const Icon = st.icon;
            const isNote = e.type === "note";
            return (
              <div key={e.id || i} className="relative flex gap-3 pb-4" data-testid="timeline-event">
                {/* linea verticale */}
                {i < visible.length - 1 && <span className="absolute left-[15px] top-8 h-full w-px bg-slate-200" />}
                <span className={`z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white ring-4 ${st.dot} ${st.ring}`}>
                  <Icon size={15} />
                </span>
                <div className={`flex-1 rounded-lg border border-slate-100 bg-white p-2.5 ${e.voided ? "opacity-50" : ""}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className={`text-sm font-medium ${e.voided ? "text-slate-400 line-through" : "text-slate-800"}`}>
                      {e.title || "Evento"}
                    </span>
                    <span className="whitespace-nowrap text-xs text-slate-400">{fmtDate(e.at)}</span>
                  </div>
                  {e.description && <div className="mt-0.5 text-xs text-slate-500">{e.description}</div>}
                  {e.meta?.body && <div className="mt-1 line-clamp-2 text-xs text-slate-400">{e.meta.body}</div>}
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {typeof e.amount === "number" && e.amount !== 0 && (
                      <Badge className={`${e.amount < 0 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{fmtAmount(e.amount)}</Badge>
                    )}
                    {e.priority && (
                      <Badge className={PRIORITY_STYLE[e.priority] || "bg-slate-100 text-slate-600"}>Priorità: {e.priority}</Badge>
                    )}
                    {e.voided && <Badge className="bg-slate-200 text-slate-500">Annullata da {e.voided_by || "admin"}</Badge>}
                    {isNote && canVoid && !e.voided && (
                      <button data-testid={`timeline-void-note-${e.note_id}`} onClick={() => onVoidNote?.(e.note_id)}
                        className="flex items-center gap-1 text-xs font-medium text-rose-500 hover:text-rose-700">
                        <Ban size={12} /> Annulla
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Timeline;
